import { motion } from 'framer-motion';
import { FormStepProps } from '../../../types/applicationTypes';

export default function Step1StudentInfo({ data, errors, updateData }: FormStepProps) {
  const studentErrors = errors?.student ?? {};

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg transition-colors ${
      hasError
        ? 'border-red-500 focus:ring-2 focus:ring-red-300 focus:border-red-500'
        : 'border-gray-300 focus:ring-2 focus:ring-amber-300 focus:border-amber-300'
    }`;

  const handleInputChange = (field: keyof typeof data.student, value: string) => {
    updateData({
      student: {
        ...data.student,
        [field]: value
      }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Section Header */}
      <div className="flex items-center mb-6">
        <div className="bg-amber-100 p-3 rounded-full mr-4">
          <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-amber-600">
          Student Information
        </h3>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <p className="text-blue-700 text-sm">
          Please provide accurate information about the student. Fields marked with an asterisk(*) are required.
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name*
            </label>
            <input
              type="text"
              value={data.student.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter First Name"
              className={inputClass(Boolean(studentErrors.firstName))}
              required
              aria-invalid={Boolean(studentErrors.firstName)}
            />
            {studentErrors.firstName && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name*
            </label>
            <input
              type="text"
              value={data.student.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter last name"
              className={inputClass(Boolean(studentErrors.lastName))}
              required
              aria-invalid={Boolean(studentErrors.lastName)}
            />
            {studentErrors.lastName && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.lastName}</p>
            )}
          </div>
        </div>

        {/* Date of Birth and Gender */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Of Birth*
            </label>
            <input
              type="date"
              value={data.student.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              className={inputClass(Boolean(studentErrors.dateOfBirth))}
              required
              aria-invalid={Boolean(studentErrors.dateOfBirth)}
            />
            {studentErrors.dateOfBirth && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.dateOfBirth}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender*
            </label>
            <div className="flex gap-6 mt-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={data.student.gender === 'female'}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-300"
                  aria-invalid={Boolean(studentErrors.gender)}
                />
                <span className="ml-2 text-sm text-gray-700">Female</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={data.student.gender === 'male'}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-300"
                  aria-invalid={Boolean(studentErrors.gender)}
                />
                <span className="ml-2 text-sm text-gray-700">Male</span>
              </label>
            </div>
            {studentErrors.gender && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.gender}</p>
            )}
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Home Address*
          </label>
          <div className="relative">
            <input
              type="text"
              value={data.student.homeAddress}
              onChange={(e) => handleInputChange('homeAddress', e.target.value)}
              placeholder="Street Address"
              className={`${inputClass(Boolean(studentErrors.homeAddress))} pr-10`}
              required
              aria-invalid={Boolean(studentErrors.homeAddress)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          {studentErrors.homeAddress && (
            <p className="mt-1 text-sm text-red-600">{studentErrors.homeAddress}</p>
          )}
        </div>

        {/* City and Zip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City*
            </label>
            <input
              type="text"
              value={data.student.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Enter City"
              className={inputClass(Boolean(studentErrors.city))}
              required
              aria-invalid={Boolean(studentErrors.city)}
            />
            {studentErrors.city && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.city}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zip code*
            </label>
            <input
              type="text"
              value={data.student.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              placeholder="Enter Zip Code"
              className={inputClass(Boolean(studentErrors.zipCode))}
              required
              aria-invalid={Boolean(studentErrors.zipCode)}
            />
            {studentErrors.zipCode && (
              <p className="mt-1 text-sm text-red-600">{studentErrors.zipCode}</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

